import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import OneSignalWebService from './OneSignalWebService';

// Configuração da API
const API_URL = process.env.REACT_APP_API_URL || 'https://all-delivery.onrender.com/api';

// Criar instância do axios
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

const TOKEN_KEY = '@DeliveryAdmin:token';
const REFRESH_TOKEN_KEY = '@DeliveryAdmin:refreshToken';
const USER_KEY = '@DeliveryAdmin:user';
const DEVICE_ID_KEY = '@DeliveryAdmin:deviceId';
let refreshRequest = null;

const getDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = `web-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

const clearStoredSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  delete api.defaults.headers.common.Authorization;
};

// Interceptor para adicionar token nas requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = String(originalRequest?.url || '');
    const isSessionEndpoint = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout']
      .some((endpoint) => requestUrl.includes(endpoint));

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isSessionEndpoint) {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (storedRefreshToken) {
        originalRequest._retry = true;

        try {
          if (!refreshRequest) {
            refreshRequest = axios
              .post(`${API_URL}/auth/refresh`, {
                refreshToken: storedRefreshToken,
                device_id: getDeviceId()
              })
              .finally(() => {
                refreshRequest = null;
              });
          }

          const response = await refreshRequest;
          const { token, refreshToken } = response.data;
          localStorage.setItem(TOKEN_KEY, token);
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
          api.defaults.headers.common.Authorization = `Bearer ${token}`;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Falha de rede/refresh nao apaga a sessao local. O usuario escolhe quando sair.
          return Promise.reject(refreshError);
        }
      }
    }

    if (error.response?.status === 401 && !isSessionEndpoint) {
      // Mantem a conta salva para que a proxima tentativa possa renovar a sessao.
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carregar usuário do storage na inicialização
  useEffect(() => {
    loadStoredData();
  }, []);

  const loadStoredData = () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const userStored = localStorage.getItem(USER_KEY);

      if (token && userStored) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(JSON.parse(userStored));
      }
    } catch (error) {
      console.error('Erro ao carregar dados armazenados:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
        device_id: getDeviceId()
      });
      const { user: userData, token, refreshToken } = response.data;

      // Permitir login de admin e motoboy
      if (!['admin_master', 'admin_lanchonete', 'cashier', 'kitchen', 'waiter', 'motoboy'].includes(userData.user_type)) {
        return { 
          success: false, 
          error: 'Acesso negado. Tipo de usuário não permitido.' 
        };
      }

      // Salvar dados no storage
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Configurar token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao fazer login';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    OneSignalWebService.logout().catch((error) => {
      console.warn('[OneSignal Web] logout:', error.message);
    });
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      axios.post(`${API_URL}/auth/logout`, {
        refreshToken,
        device_id: getDeviceId()
      }).catch(() => {});
    }
    clearStoredSession();
    setUser(null);
    window.location.href = window.location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
  };

  const verifyToken = async () => {
    try {
      const response = await api.get('/auth/verify');
      const { user: userData } = response.data;

      // Atualizar usuário no storage
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      // Verificacao indisponivel nao deve encerrar a sessao automaticamente.
      return { success: false, error: 'Sessão expirada' };
    }
  };

  const isAdminMaster = () => {
    return user?.user_type === 'admin_master';
  };

  const isAdminLanchonete = () => {
    return user?.user_type === 'admin_lanchonete';
  };

  const isStoreStaff = () => ['admin_lanchonete', 'cashier', 'kitchen', 'waiter'].includes(user?.user_type);
  const hasRole = (...roles) => roles.includes(user?.user_type);

  const getLanchoneteId = () => {
    return user?.lanchonete_id;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        verifyToken,
        api,
        isAdminMaster,
        isAdminLanchonete,
        isStoreStaff,
        hasRole,
        getLanchoneteId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export { api };
